import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Sprite} from 'pixi.js';
import {CheatModule} from '@gongxigames/framework/slot/modules/cheatpage/CheatModule';
import {ILogic} from '@gongxigames/framework/slot/types';
import {ResourceLoader} from '@gongxigames/framework/core';
import {ReelConstants} from '@gongxigames/framework/slot/utils';

export interface ImgSymbolData {
    image: HTMLImageElement;
    imageName: string;
    imageId: number;
    reel: number;
    row: number;
}

export class ReelsetMatrix {
    protected _cheat: CheatModule;
    protected _logic: ILogic;
    protected _resourceLoader: ResourceLoader;
    protected _reels: HTMLElement[] = [];
    protected _reelstrips: number[][] = [];
    protected _imagesData: any;
    protected _possibleImagesOnReels: number[][][] = [];
    protected _stopPositions: number[] = [];
    protected _filter!: HTMLElement;

    constructor(cheat: CheatModule) {
        this._cheat = cheat;
        this._logic = cheat.logic;
        this._resourceLoader = cheat.resourceLoader;
        this._imagesData = cheat.getImagesData();

        this.initElements();
        this.initReelsetMatrix();
        this.initStopPositions();
    }

    public getReelStrips(): void {
        this._reelstrips.length = 0;
        this._logic.getNextReelSet().getReels().forEach(strip => this._reelstrips.push(strip.getSymbols()));

        if (this._logic.previousAction !== this._logic.nextAction) {
            this.resetFilter();
        }
    }

    protected initReelsetMatrix(): void {
        const reelset = document.getElementById('realReelset')!;
        const reelsAmount = ReelConstants.REELS_AMOUNT;
        const symbolsAmount = ReelConstants.ROW_AMOUNT;

        this.getReelStrips();
        this.updatePossibleImagesOnReels();

        reelset.className = `reels-${reelsAmount}`;

        for (let i = 0; i < reelsAmount; i++) {
            const reel = document.createElement('div');

            this._reels.push(reel);

            reel.className = `reel reel${i}`;

            for (let j = 0; j < symbolsAmount; j++) {
                this.addSymbolToMatrix(i, j);
            }

            reelset.appendChild(reel);
        }

        this.initMatrixSymbols();
        this.initMatrixButtons();
        this.initFilterEvents();
    }

    protected initFilterEvents(): void {
        this._filter.onclick = this.resetFilter.bind(this);
    }

    protected initStopPositions(): void {
        this._stopPositions.length = 0;

        for (let i = 0; i < ReelConstants.REELS_AMOUNT; i++) {
            this._stopPositions.push(-1);
        }
    }

    protected initElements(): void {
        this._filter = document.getElementById('reelsetFilter')!;
    }

    protected updatePossibleImagesOnReels(): void {
        this._reelstrips.forEach((strip, reelIndex) => {
            const possibleImagesOnReel: number[] = [];

            this._possibleImagesOnReels[reelIndex] = [];

            strip.forEach(symbol => possibleImagesOnReel.indexOf(symbol) === -1 && possibleImagesOnReel.push(symbol));

            for (let rowIndex = 0; rowIndex < ReelConstants.ROW_AMOUNT; rowIndex++) {
                this._possibleImagesOnReels[reelIndex][rowIndex] = this.sortArray(possibleImagesOnReel);
            }
        });
    }

    protected addSymbolToMatrix(reelNumber: number, rowNumber: number): void {
        const symbol = document.createElement('div');
        const emptyImage = document.createElement('div');
        const image = document.createElement('img');

        symbol.className = `symbol reel-${reelNumber} row-${rowNumber}`;
        emptyImage.className = 'picked';
        symbol.appendChild(emptyImage);
        symbol.appendChild(image);

        this._reels[reelNumber].appendChild(symbol);
    }

    protected initMatrixSymbols(): void {
        const pickedSymbols = document.querySelectorAll('#realReelset .picked') as any;

        for (const symbol of pickedSymbols) {
            symbol.onclick = (e: any) => {
                const imgSymbolData = this.getImgSymbolData(e.target);

                if (e.target.nodeName === 'DIV') {
                    e.target.classList.remove('picked');
                    e.target.nextElementSibling.classList.add('picked');
                    this.setNextImageSymbol(imgSymbolData);
                } else {
                    if (this.isLastImageSymbol(imgSymbolData)) {
                        this.resetSymbol(e);
                    } else {
                        this.setNextImageSymbol(imgSymbolData, false);
                    }
                }
                this.initMatrixSymbols();
            };
            symbol.oncontextmenu = (e: any) => {
                this.setPreviousImageSymbol(e);
            };
        }
    }

    protected resetSymbol(e: any): void {
        e.preventDefault();

        const data = this.getImgSymbolData(e.target);

        e.target.className = '';
        e.target.parentElement.children[0].classList.add('picked');

        this.updatePossibleSymbolsOnReel(data);
        this.updateReelStopPositions(data);
    }

    protected initMatrixButtons(): void {
        this.setApplyBtn();
        this.setRejectBtn();
        this.setResetPositionsBtn();
    }

    protected setApplyBtn(): void {
        const applyBtn = document.getElementById('applyButton')!;

        applyBtn.onclick = () => {
            const stopPositions = [...this._stopPositions];

            this.checkAndSetUserStopPositions(stopPositions);

            this._filter.innerHTML = '';
            this._filter.innerHTML += `<p>Stop positions: ${stopPositions}</p>`;

            this.isNextActionSpin() && this.setFilter(stopPositions);
        };
    }

    protected setResetPositionsBtn(): void {
        const resetBtn = document.getElementById('resetButton')!;

        resetBtn.onclick = () => {
            const input = document.querySelector('#stopPositions input') as HTMLInputElement;

            input.value = '';
        };
    }

    protected checkAndSetUserStopPositions(stopPositions: number[]): void {
        const input = document.querySelector('#stopPositions input') as HTMLInputElement;
        const userStopPositions = input.value;

        input.classList.remove('error');

        if (userStopPositions) {
            const stopPosArr = userStopPositions.split(',').map(pos => isNaN(parseInt(pos, 10)) ? -1 : parseInt(pos, 10));

            if (this.isAppropriatePositions(stopPosArr)) {
                this.resetMatrix();

                this.initStopPositions();

                stopPosArr.forEach((pos, index) => stopPositions[index] = pos);
            } else {
                input.classList.add('error');
            }
        }
    }

    protected isAppropriatePositions(stopPosArr: number[]): boolean {
        let isCorrectPositions = true;

        stopPosArr.forEach((stopPos, index) => {
            if (this._reelstrips[index] && stopPos >= this._reelstrips[index].length || stopPos < -1) {
                isCorrectPositions = false;
            }
        });

        return isCorrectPositions;
    }

    protected isNextActionSpin(): boolean {
        return this._logic.nextAction === Action.SPIN ||
            this._logic.nextAction === Action.FREESPIN ||
            this._logic.nextAction === Action.RESPIN;
    }

    protected setRejectBtn(): void {
        document.getElementById('rejectButton')!.onclick = this.resetFilter.bind(this);
    }

    protected resetFilter(): void {
        this._filter.innerHTML = '';
        this.resetMatrix();
        this.initStopPositions();
        this.setFilter();
        this.updatePossibleImagesOnReels();
    }

    protected setFilter(stopPositions: number[] | null = null): void {
        this._logic.setFilterForStopPositions(stopPositions);
    }

    protected resetMatrix(): void {
        const allSymbols = document.querySelectorAll('#realReelset .symbol') as any;
        const pickedSymbols = document.querySelectorAll('#realReelset .picked') as any;

        for (const item of pickedSymbols) {
            item.className = '';
        }

        for (const symbol of allSymbols) {
            symbol.children[0].classList.add('picked');
        }
    }

    protected updateReelStopPositions(data: ImgSymbolData): void {
        const strip = this._reelstrips[data.reel];
        const chosenSymbolIds = this.getChosenSymbols(data);
        const matchedPositions = [];

        for (let i = 0; i < strip.length; i++) {
            let currentPos = 0;

            const isMatched = chosenSymbolIds.every((symbolId: number) => {
                const isMatched = symbolId === this.getSymbolFromStripPosition(strip, i, currentPos) || symbolId === -1;

                currentPos++;

                return isMatched;
            });

            isMatched && matchedPositions.push(i);
        }

        this._stopPositions[data.reel] = this.getRandomStopPosition(matchedPositions);
    }

    protected getRandomStopPosition(matchedPositions: number[]): number {
        return matchedPositions.length ? matchedPositions[Math.floor(Math.random() * matchedPositions.length)] : -1;
    }

    protected updatePossibleSymbolsOnReel(data: ImgSymbolData): void {
        const chosenSymbolIds = this.getChosenSymbols(data);
        const strip = this._reelstrips[data.reel];
        const possibleImagesOnReel: number[][] = [];
        let symbolNumberOnReel = 0;

        chosenSymbolIds.forEach(() => {
            const tmpChosenSymbolIds = [...chosenSymbolIds];

            tmpChosenSymbolIds[symbolNumberOnReel] = -1;

            possibleImagesOnReel[symbolNumberOnReel] = [];

            for (let i = 0; i < strip.length; i++) {
                let currentPos = 0;

                const isMatched = tmpChosenSymbolIds.every((symbolId: number) => {
                    const isMatched = symbolId === this.getSymbolFromStripPosition(strip, i, currentPos) || symbolId === -1;

                    currentPos++;

                    return isMatched;
                });

                const currentSymbol = this.getSymbolFromStripPosition(strip, i, symbolNumberOnReel);

                if (isMatched && currentSymbol >= 0 && possibleImagesOnReel[symbolNumberOnReel].indexOf(currentSymbol) === -1) {
                    possibleImagesOnReel[symbolNumberOnReel].push(currentSymbol);
                }

                this._possibleImagesOnReels[data.reel][symbolNumberOnReel] = this.sortArray(possibleImagesOnReel[symbolNumberOnReel]);
            }

            symbolNumberOnReel++;
        });
    }

    protected getSymbolFromStripPosition(strip: number[], stripPosition: number, currentPosition: number): number {
        return stripPosition + currentPosition >= strip.length ? strip[Math.abs(strip.length - (stripPosition + currentPosition))] :
            strip[stripPosition + currentPosition];
    }

    protected getChosenSymbols(data: ImgSymbolData): number[] {
        const symbolsOnReel = data.image.parentElement!.parentElement!.children;
        const chosenSymbolIds = [];

        for (let i = 0; i < symbolsOnReel.length; i++) {
            const pickedImage = symbolsOnReel[i].querySelector('img.picked');
            let imageId = -1;

            if (pickedImage) {
                const imageName = pickedImage.classList[pickedImage.classList.length - 1];

                imageId = this.getImageId(imageName);
            }

            chosenSymbolIds[i] = imageId;
        }

        return chosenSymbolIds;
    }

    protected isLastImageSymbol(data: ImgSymbolData): boolean {
        const possibleImages = this._possibleImagesOnReels[data.reel][data.row];

        return possibleImages.indexOf(data.imageId) === possibleImages.length - 1;
    }

    protected setNextImageSymbol(data: ImgSymbolData, isFirst = true): void {
        const possibleImages = this._possibleImagesOnReels[data.reel][data.row];
        const currentPos = possibleImages.indexOf(data.imageId);
        const imageNumber = possibleImages[isFirst ? 0 : currentPos + 1];

        this.setImageSymbol(imageNumber, data);
    }

    protected setPreviousImageSymbol(e: any): void {
        e.preventDefault();
        const data: ImgSymbolData = this.getImgSymbolData(e.target);
        const possibleImages = this._possibleImagesOnReels[data.reel][data.row];
        const currentPos = possibleImages.indexOf(data.imageId);

        if (currentPos <= 0) {
            this.resetSymbol(e);

            return;
        }
        const imageNumber = possibleImages[currentPos - 1];

        this.setImageSymbol(imageNumber, data);
    }

    protected setImageSymbol(imageNumber: number, data: ImgSymbolData): void {
        const nextImageName = this._imagesData[imageNumber];
        const classList = data.image.classList;

        classList[1] && classList.remove(classList[1]);
        classList.add(nextImageName);

        data.image.src = this._cheat.getImageSource(nextImageName);

        this.updatePossibleSymbolsOnReel(data);
        this.updateReelStopPositions(data);
    }

    protected getImgSymbolData(target: any): ImgSymbolData {
        const image = target.nodeName === 'DIV' ? target.nextElementSibling : target;
        const imageName = target.classList[target.classList.length - 1];
        const imageId = this.getImageId(imageName);
        const reel = target.parentElement.classList[1].substr(-1);
        const row = target.parentElement.classList[2].substr(-1);

        return {
            image,
            imageName,
            imageId,
            reel,
            row
        };
    }

    protected getImageId(imageName: string): number {
        let imageId = 0;

        if (imageName !== 'picked') {
            for (const index in this._imagesData) {
                if (this._imagesData.hasOwnProperty(index) && this._imagesData[index] === imageName) {
                    imageId = +index;
                }
            }
        }

        return imageId;
    }

    protected sortArray(arr: number[]): number[] {
        return arr.sort((x, y) => x - y);
    }

    public extractTextureSRC(name: string): string {
        const texture = this._resourceLoader.getTexture(name);
        const sprite = new Sprite(texture);

        return this._cheat.system.app.renderer.plugins.extract.image(sprite).src;
    }
}
